import React, { FC, useEffect, useState } from 'react';
import { IModalWindow, ModalWindow } from '../../components/modal/ModalWindow';
import { MFANotificationModal } from '../../components/login/MFANotificationModal';
import { SecurityAppForm } from '../../components/profileSettings/forms/SecurityAppForm';
import { VerificationCodeForm } from '../../components/profileSettings/forms/VerificationCodeForm';
import {
	fetchSecurityInformation,
	setTwoFactorAuthentication,
	verifyCodeAuthentication,
} from '../../store/identyti/actions/security';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleCheck, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { ButtonComponent } from '../../components/ButtonComponent';
import { LinkComponent } from '../../components/LinkComponent';
import { useTranslation } from 'react-i18next';
import { useIdentity } from '../../store/identyti/identity-context';
import { ITwoFactorAuthParams, IVerificationParams } from '../../components/profileSettings/AccountSecurityAddKey';

export const MfaLayout: FC = () => {
	const { t } = useTranslation();

	const { identityState: { isCodeVerificationLoading }, dispatch } = useIdentity();

	const enforcedMFAData = JSON.parse(localStorage.getItem('enforcedMFAData'));

	const skipAvailable = enforcedMFAData?.skipAvailable;
	// const skipAvailable = true;
	const daysLeft = enforcedMFAData?.daysLeft;
	const email = localStorage.getItem('userEmail');
	const token = localStorage.getItem('accessToken');

	const [isCodeModalOpen, setIsCodeModalOpen] = useState<boolean>(false);
	const [isMFANotificationModalOpen, setMFANotificationModalOpen] = useState<boolean>(false);
	const [modalState, setModalState] = useState<IModalWindow>({
		isOpen: false
	});
	const [qRCodeModalState, setQRCodeModalState] = useState({
		isOpen: false,
		accountName: '',
		verificationCode: '',
	});
	const closeNotificationModal = (): void | boolean => {
		setMFANotificationModalOpen(false);
		if (skipAvailable) {
			localStorage.removeItem('enforcedMFAData');
		}
	};

	const closeQRCodeModal = (): void => setQRCodeModalState(prev => ({ ...prev, isOpen: false }));

	const closeModal = (): void => setModalState(prev => ({ ...prev, isOpen: false }));

	useEffect(() => {
		if (daysLeft) {
			setMFANotificationModalOpen(true);
		}
	}, []);

	const handleEnroll = async (): Promise<void> => {
		const params: ITwoFactorAuthParams = {
			twoFactorEnabled: true,
			isUpdate: false,
			token,
			email,
		};
		const res = await setTwoFactorAuthentication(dispatch, params);
		const verificationCode = res?.data?.verificationCode;
		const accountName = res?.data?.name;
		closeNotificationModal();
		setQRCodeModalState({
      isOpen: true,
      verificationCode,
      accountName
    });
	};

	const handleConfirmCode = async (code: string): Promise<void> => {
		const params: IVerificationParams = {
			code,
			token,
			email,
		};
		await verifyCodeAuthentication(dispatch, params, handleSuccessModal, handleErrorModal);
	};

	const handleSuccessModal = (): void => {
		const handleDone = async (): Promise<void> => {
			await fetchSecurityInformation(dispatch, email);
			localStorage.removeItem('enforcedMFAData');
			closeModal();
		};
		setIsCodeModalOpen(false);
		setModalState({
      isOpen: true,
      modalTitle: 'Use an authenticator app',
      contentClassName: 'd-block',
      minHeight: 210,
      backdrop: 'static',
      onClose: handleDone,
      content: (
        <>
          <FontAwesomeIcon icon={faCircleCheck} width={32} className="d-block mx-auto h-32 mt-10"/>
          <div className="fs-6 w-450px mx-auto text-center mt-5">
            From now on, whenever you sign in to your account, you’ll need to enter both your password and also an authentication code generated by Google Authenticator.
          </div>
          <ButtonComponent
            buttonName="Done"
            onButtonClick={handleDone}
            additionalClass="mt-10 d-block mx-auto"
          />
        </>
      )
    });
	};

	const handleErrorModal = (): void => {
		setIsCodeModalOpen(false);
		setModalState({
      isOpen: true,
      modalTitle: 'Use an authenticator app',
      content: (
        <div>
          <div className="fs-3 text-danger text-center mb-5">
            <FontAwesomeIcon icon={faTriangleExclamation} className="me-1"/>
            Incorrect verification code.
          </div>
          <div className="fs-6 px-15 text-center">
            Having trouble entering your code? Please, contact with {' '}
            <LinkComponent text="administrator." link='mailto:support@flex-charge.com'/>
          </div>

        </div>
      ),
      confirmButtonText: t('PARTNERS.FAIL_BTN_NAME'),
      minHeight: 100,
      onConfirm: () => {
        closeModal();
        setIsCodeModalOpen(true);
      },
      onClose: closeModal
    });
	};

	return (
		<>
			<ModalWindow
				{...modalState}
				onClose={closeModal}
			/>
			<ModalWindow
				isOpen={isMFANotificationModalOpen}
				modalTitle="Don't get locked out"
				backdrop="static"
				onClose={closeNotificationModal}
				showCloseButton={skipAvailable}
				content={
					<MFANotificationModal
						onClose={closeNotificationModal}
						onEnroll={handleEnroll}
					/>
				}
			/>

			<ModalWindow
				isOpen={qRCodeModalState.isOpen}
				modalTitle="Use an authenticator app"
				backdrop="static"
				contentClassName="d-block"
				minHeight={350}
				onClose={closeQRCodeModal}
				showCloseButton={skipAvailable}
				content={
					<SecurityAppForm
						accountName={qRCodeModalState.accountName}
						code={qRCodeModalState.verificationCode}
						showCloseButton={skipAvailable}
						closeModal={closeQRCodeModal}
						onConfirm={(): void => {
							closeQRCodeModal();
							setIsCodeModalOpen(true);
						}}
					/>
				}
			/>

			<ModalWindow
				isOpen={isCodeModalOpen}
				modalTitle="Use an authenticator app"
				backdrop="static"
				contentClassName='d-block'
				minHeight={200}
				showCloseButton={skipAvailable}
				onClose={(): void => setIsCodeModalOpen(false)}
				content={
					<VerificationCodeForm
						onBackClick={(): void => {
							setIsCodeModalOpen(false);
							setQRCodeModalState(prev => ({ ...prev, isOpen: true }));
						}}
						onContinue={handleConfirmCode}
						isLoading={isCodeVerificationLoading}
					/>
				}
			/>
		</>
	);
};